import { AlertTriangle, Check, Copy } from 'lucide-react'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { Button } from '../ui/button'

export type AppErrorMessages = {
  title: Record<string, string>
  body: Record<string, string>
  reference: string
  referenceHint: string
  details: string
  retry: string
  reload: string
  signIn: string
  back: string
  copy: string
  copied: string
  loading: string
  support?: string
}

export type AppErrorValue = {
  kind?: string
  code?: string
  status?: number
  message?: string
  reference?: string
  isRetryable?: boolean
}

export type AppErrorProps = {
  error: unknown
  messages: AppErrorMessages
  retry?: () => void
  reference?: string
}

const AppErrorMessagesContext = createContext<AppErrorMessages | null>(null)

export function AppErrorMessagesProvider({
  children,
  messages,
}: {
  children: ReactNode
  messages: AppErrorMessages
}) {
  return (
    <AppErrorMessagesContext.Provider value={messages}>{children}</AppErrorMessagesContext.Provider>
  )
}

export function useAppErrorMessages(): AppErrorMessages {
  const messages = useContext(AppErrorMessagesContext)
  if (!messages) throw new Error('AppErrorMessagesProvider is required')
  return messages
}

function readError(value: unknown): AppErrorValue {
  if (typeof value !== 'object' || value === null) return {}
  const candidate = value as Record<string, unknown>
  return {
    kind: typeof candidate.kind === 'string' ? candidate.kind : undefined,
    code: typeof candidate.code === 'string' ? candidate.code : undefined,
    status: typeof candidate.status === 'number' ? candidate.status : undefined,
    message: typeof candidate.message === 'string' ? candidate.message : undefined,
    reference: typeof candidate.reference === 'string' ? candidate.reference : undefined,
    isRetryable: candidate.isRetryable === true,
  }
}

const CLIENT_REFERENCE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

function createClientErrorReference(): string {
  const values = new Uint32Array(8)
  globalThis.crypto?.getRandomValues(values)

  return Array.from(values, (value) => CLIENT_REFERENCE_ALPHABET[value % 32]).join('')
}

export function getErrorReference(value: unknown): string | undefined {
  const error = readError(value)
  if (error.reference) return error.reference
  if (error.kind || error.status !== undefined || error.code) return undefined
  return createClientErrorReference()
}

function isTechnicalMessage(message?: string | null): boolean {
  if (!message) return false
  return /SQLSTATE|QueryException|PDOException|ErrorException|relation "|violates|Internal Server Error|syntax error|Failed to fetch/i.test(
    message,
  )
}

export function AppError({ error: value, messages, retry, reference }: AppErrorProps) {
  const error = readError(value)
  // The reference is minted by whichever boundary caught the error, so it is
  // the same value that gets recorded. This component only displays what it
  // is handed or what a server error carries - it never invents one.
  const [displayReference] = useState(() => reference ?? readError(value).reference)
  const renderError = !error.kind
  const kind = error.kind ?? 'unknown'
  const isConflict = kind === 'conflict'
  const isSuspended = kind === 'forbidden' && error.code === 'group_suspended'
  const title = messages.title[isSuspended ? 'suspended' : kind] ?? messages.title.generic
  const isTechnical = isTechnicalMessage(error.message)
  const body =
    (isConflict || (kind === 'forbidden' && error.message)) && !isTechnical
      ? (error.message ?? messages.body.generic)
      : (messages.body[isSuspended ? 'suspended' : kind] ?? messages.body.generic)
  const needsSupportSession =
    kind === 'forbidden' && error.message?.toLowerCase().includes('support session')
  const canRetry =
    Boolean(retry) && (renderError || error.isRetryable || kind === 'server' || kind === 'unknown')
  const canReload = renderError || kind === 'server' || kind === 'unknown'
  const [copied, setCopied] = useState(false)
  const [retrying, setRetrying] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(timer)
  }, [copied])

  const copyReference = async () => {
    if (!displayReference || !navigator.clipboard) return
    await navigator.clipboard.writeText(displayReference)
    setCopied(true)
  }

  const handleRetry = async () => {
    if (!retry) return
    setRetrying(true)
    try {
      await retry()
    } finally {
      setRetrying(false)
    }
  }

  return (
    <section
      role="alert"
      className="mx-auto grid min-h-full max-w-prose content-center gap-4 p-6 text-start"
    >
      <AlertTriangle aria-hidden="true" className="size-6 text-danger" />
      <div className="grid gap-2">
        <h1 className="font-display text-title">{title}</h1>
        <p className="text-body text-muted-foreground">{body}</p>
      </div>
      {displayReference ? (
        <div className="grid gap-1 justify-self-start rounded-sm border border-border px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{messages.reference}</span>
            <code className="select-all text-identifier">{displayReference}</code>
            {typeof navigator !== 'undefined' && navigator.clipboard ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={copied ? messages.copied : messages.copy}
                onClick={copyReference}
              >
                {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              </Button>
            ) : null}
          </div>
          <span className="text-xs text-muted-foreground">{messages.referenceHint}</span>
        </div>
      ) : null}
      {/* The primary action is last in its group (DESIGN.md "Buttons"), so
          the outline actions come first. */}
      <div className="flex flex-wrap gap-2">
        {kind === 'notFound' ||
        (kind === 'forbidden' && !isSuspended && error.code !== 'module_not_enabled') ? (
          <Button variant="outline" onClick={() => window.history.back()}>
            {messages.back}
          </Button>
        ) : null}
        {canReload ? (
          <Button
            disabled={retrying}
            variant={canRetry ? 'outline' : 'primary'}
            onClick={() => window.location.reload()}
          >
            {messages.reload}
          </Button>
        ) : null}
        {needsSupportSession && messages.support ? (
          <Button asChild>
            <a href="/support">{messages.support}</a>
          </Button>
        ) : null}
        {kind === 'unauthorized' ? (
          <Button
            onClick={() => {
              window.location.href = '/login'
            }}
          >
            {messages.signIn}
          </Button>
        ) : null}
        {canRetry ? (
          <Button disabled={retrying} onClick={handleRetry}>
            {retrying ? messages.loading : messages.retry}
          </Button>
        ) : null}
      </div>
      <details className="text-sm text-muted-foreground">
        <summary className="cursor-pointer">{messages.details}</summary>
        <p className="mt-2 break-words text-identifier">
          {error.message ?? messages.body.generic}
          {error.status ? ` (${error.status})` : ''}
        </p>
      </details>
    </section>
  )
}

export type ErrorBoundaryProps = {
  children: ReactNode
  onError?: (error: unknown) => void
  messages: AppErrorMessages
}
